import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchEventBySlug, joinWaitlist, downloadCalendar } from '../utils/api';
import { formatDate } from '../utils/api';
import RSVPForm from '../components/RSVPForm';
import CapacityIndicator from '../components/CapacityIndicator';
import { Calendar, MapPin, Clock, Download, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AdminBanner } from '../components/AdminBanner';

export default function EventDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRSVPForm, setShowRSVPForm] = useState(false);
  const [rsvpSuccess, setRSVPSuccess] = useState(false);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(null);

  useEffect(() => {
    loadEvent();
  }, [slug]);

  const loadEvent = async () => {
    try {
      const data = await fetchEventBySlug(slug);
      setEvent(data);
    } catch (error) {
      console.error('Failed to load event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVPSuccess = (result) => {
    setShowRSVPForm(false);
    setRSVPSuccess(true);
  };

  const handleJoinWaitlist = async () => {
    const firstName = prompt('Enter your first name:');
    const lastName = prompt('Enter your last name:');
    const email = prompt('Enter your email:');
    
    if (!firstName || !lastName || !email) return;
    
    setJoiningWaitlist(true);
    
    try {
      const result = await joinWaitlist(event.id, {
        first_name: firstName,
        last_name: lastName,
        email: email
      });
      
      setWaitlistSuccess(result);
      alert(`Success! You're #${result.position} on the waitlist.`);
    } catch (err) {
      alert('Failed to join waitlist: ' + err.message);
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleDownloadCalendar = () => {
    downloadCalendar(event.id, event.slug);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <p className="text-[#4C5A70]">Loading event...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-[#0B2240] mb-4">Event not found</h1>
          <button
            onClick={() => navigate('/events')}
            className="text-[#1FA3A3] hover:underline"
          >
            ← Back to events
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <title>{event.title} | KGOB Events</title>
      <meta name="description" content={event.description} />

      <AdminBanner />

      <div className="min-h-screen bg-[#F7F6F2]">
        {/* Back Button */}
        <div className="bg-white border-b border-[#E1DFD7]">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-4">
            <button
              onClick={() => navigate('/events')}
              className="text-[#4C5A70] hover:text-[#0B2240] transition-colors"
              data-testid="back-to-events-btn"
            >
              ← Back to Events
            </button>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* Event Header */}
            <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7] mb-6">
              <div className="flex gap-2 mb-4">
                <span className="px-3 py-1 bg-[#0B2240] text-white text-xs font-semibold rounded-full uppercase tracking-wider">
                  {event.type.replace('_', ' ')}
                </span>
                <span className="px-3 py-1 bg-[#EDEBE6] text-[#0B2240] text-xs rounded-full">
                  {event.location_city}
                </span>
              </div>

              <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0B2240] mb-4">
                {event.title}
              </h1>

              <div className="space-y-3 text-[#4C5A70] mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(event.start_datetime, event.timezone)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>{event.location_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>
                    {new Date(event.start_datetime).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      timeZone: event.timezone,
                      timeZoneName: 'short'  // Shows EST, PST, etc.
                    })}
                    {' - '}
                    {new Date(event.end_datetime).toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      timeZone: event.timezone,
                      timeZoneName: 'short'  // Shows EST, PST, etc.
                    })}
                  </span>
                </div>
              </div>

              <div className="text-[#4C5A70] leading-relaxed mb-6 prose prose-sm max-w-none
                prose-headings:text-[#0B2240] prose-headings:font-heading prose-headings:font-semibold
                prose-p:text-[#4C5A70] prose-p:my-2
                prose-strong:text-[#0B2240] prose-strong:font-semibold
                prose-em:text-[#4C5A70] prose-em:italic
                prose-ul:my-3 prose-ul:space-y-2
                prose-li:text-[#4C5A70] prose-li:marker:text-[#1FA3A3]
                prose-a:text-[#1FA3A3] prose-a:underline
              ">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {event.description}
                </ReactMarkdown>
              </div>

              {event.parking_instructions && (
                <div className="bg-[#F7F6F2] rounded-lg p-4 border border-[#E1DFD7]">
                  <p className="text-sm text-[#4C5A70]">
                    <strong className="text-[#0B2240]">Parking:</strong> {event.parking_instructions}
                  </p>
                </div>
              )}
            </div>

            {/* Capacity Indicator */}
            {event.requires_rsvp && event.capacity && (
              <CapacityIndicator
                capacity={event.capacity}
                totalRSVPs={event.total_rsvps}
                spotsAvailable={event.spots_available}
              />
            )}

            {/* Donation Link Button (if enabled) */}
            {event.enable_donation_link && event.donation_link_url && (
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#C7A52A] rounded-2xl p-8 shadow-soft mb-6">
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Support This Event</h3>
                <p className="text-white/90 mb-6">Help make this event possible with your generous contribution.</p>
                <a
                  href={event.donation_link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full h-12 bg-white text-[#D4AF37] rounded-[12px] font-semibold hover:bg-[#F7F6F2] transition-colors flex items-center justify-center gap-2"
                  data-testid="donation-link-btn"
                >
                  {event.donation_link_text || 'Support This Cause'}
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )}

            {/* RSVP Section */}
            {event.requires_rsvp && !rsvpSuccess && !showRSVPForm && !waitlistSuccess && (
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7] mb-6">
                {event.is_at_capacity ? (
                  <div>
                    <h2 className="font-heading text-2xl font-semibold text-[#0B2240] mb-4">Event at Capacity</h2>
                    <p className="text-[#4C5A70] mb-6">This event is currently full. Join the waitlist to be notified if spots open.</p>
                    <button
                      onClick={handleJoinWaitlist}
                      disabled={joiningWaitlist}
                      className="w-full h-12 bg-[#0B2240] text-white rounded-[12px] font-medium hover:bg-[#0A1D35] transition-colors disabled:opacity-50"
                      data-testid="join-waitlist-btn"
                    >
                      {joiningWaitlist ? 'Joining...' : `Join Waitlist (${event.total_waitlist})`}
                    </button>
                  </div>
                ) : (
                  <div>
                    <h2 className="font-heading text-2xl font-semibold text-[#0B2240] mb-4">Register for This Event</h2>
                    <p className="text-[#4C5A70] mb-6">Secure your spot and receive confirmation with calendar invite.</p>
                    <button
                      onClick={() => setShowRSVPForm(true)}
                      className="w-full h-12 bg-[#0B2240] text-white rounded-[12px] font-medium hover:bg-[#0A1D35] transition-colors focus-visible:ring-2 focus-visible:ring-[#1FA3A3] focus-visible:outline-none"
                      data-testid="show-rsvp-form-btn"
                    >
                      RSVP Now
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Waitlist Success */}
            {waitlistSuccess && (
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-soft border border-[#1FA3A3]"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="waitlist-success-message"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-white">#{waitlistSuccess.position}</span>
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-[#0B2240] mb-2">You're on the Waitlist!</h2>
                  <p className="text-[#4C5A70] mb-6">
                    You're position #{waitlistSuccess.position}. We'll email you if a spot opens.
                  </p>
                  <button
                    onClick={() => navigate('/events')}
                    className="text-[#1FA3A3] hover:underline"
                  >
                    ← Back to Events
                  </button>
                </div>
              </motion.div>
            )}

            {/* Add to Calendar (for non-RSVP events) */}
            {!event.requires_rsvp && (
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7] mb-6">
                <h2 className="font-heading text-2xl font-semibold text-[#0B2240] mb-4">Add to Your Calendar</h2>
                <p className="text-[#4C5A70] mb-6">No RSVP required - just drop in! Add to your calendar as a reminder.</p>
                <button
                  onClick={handleDownloadCalendar}
                  className="w-full h-12 bg-[#0B2240] text-white rounded-[12px] font-medium hover:bg-[#0A1D35] transition-colors flex items-center justify-center gap-2"
                  data-testid="download-calendar-btn"
                >
                  <Download className="w-5 h-5" />
                  Add to Calendar
                </button>
              </div>
            )}

            {/* RSVP Form */}
            {showRSVPForm && (
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-[#E1DFD7] mb-6">
                <button
                  onClick={() => setShowRSVPForm(false)}
                  className="text-[#4C5A70] hover:text-[#0B2240] mb-4"
                  data-testid="close-rsvp-form-btn"
                >
                  ← Back
                </button>
                <RSVPForm eventId={event.id} onSuccess={handleRSVPSuccess} allowGuests={event.allow_guests} />
              </div>
            )}

            {/* Success Message */}
            {rsvpSuccess && (
              <motion.div
                className="bg-white rounded-2xl p-8 shadow-soft border border-[#1FA3A3]"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="rsvp-success-message"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#1FA3A3] rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-[#0B2240] mb-2">You're Registered!</h2>
                  <p className="text-[#4C5A70] mb-6">
                    Confirmation email sent. Check your inbox for event details and calendar invite.
                  </p>
                  <button
                    onClick={() => navigate('/events')}
                    className="text-[#1FA3A3] hover:underline"
                  >
                    ← Back to Events
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
